import isFunction from 'lodash/isFunction';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { type GetInitialPaymentData, type OnShippingContactSelected } from '../../types/ExternalContract';
import { type WidgetMeta } from '../../types/WidgetProps';
import { amountToLong } from '../../utils/amountToLong';
import { type BiLogger } from '../../components/Provider/BiLogger/BiLogger';
import { isValidShippingError, ShippingError } from '../../types/Shipping';
import { applePayCallBackReceived, applePayShippingAddressNotAccepted } from '../../utils/bi/events/applePay';
import { getRestrictedContact, transformPaymentBreakdownToApplePay } from './applePayDataMapper';
import { createApplePayErrorConstructorParams } from './createApplePayError';

/**
 * Handles shipping contact change
 * @param consumerCallback OnShippingContactSelected
 * @param data Object
 */
export const shippingContactSelectedHandler =
  (
    consumerCallback: OnShippingContactSelected,
    data: {
      paymentLabel: string;
      paymentAmount: string;
      meta: WidgetMeta;
      currencyCode: string;
    },
    biLogger: BiLogger,
    getInitialPaymentData: GetInitialPaymentData,
  ) =>
  async (event: ApplePayJS.ApplePayShippingContactSelectedEvent) => {
    try {
      let biShippingContact = '';
      try {
        biShippingContact = JSON.stringify(event.shippingContact);
      } catch {}

      // when this callback invoked requestShipping is always true
      // so we don't need to check it
      if (isFunction(getInitialPaymentData)) {
        await getInitialPaymentData();
      }

      const response = await consumerCallback(getRestrictedContact(event.shippingContact));
      const { error, paymentAmount, paymentBreakdown, shippingMethods } = response;
      const isError = isValidShippingError(error);
      const newTotal = { label: data.paymentLabel, amount: isError ? data.paymentAmount : paymentAmount };
      const newLineItems = transformPaymentBreakdownToApplePay(paymentBreakdown);
      biLogger.report(applePayCallBackReceived, {
        checkoutType: 'express',
        newValue: biShippingContact,
        amountLong: amountToLong(newTotal.amount),
        appInstanceId: data.meta.appInstanceId,
        fieldName: 'shippingContact',
        currency: data.currencyCode,
        paymentmethod: PaymentMethod.ApplePay,
        paymentProvider: PaymentProvider.WixPayUS,

        orderSnapshotId: undefined as any, // TODO[bi]
        transactionId: undefined as any, // TODO[bi]
        isPartner: undefined as any, // TODO[bi]
        amount: undefined as any, // TODO[bi]
      });

      if (isError) {
        biLogger.report(applePayShippingAddressNotAccepted, {
          checkoutType: 'express',
          amountLong: amountToLong(newTotal.amount),
          appInstanceId: data.meta.appInstanceId,
          currency: data.currencyCode,
          paymentMethod: PaymentMethod.ApplePay,
          paymentProvider: PaymentProvider.WixPayUS,

          orderSnapshotId: undefined as any, // TODO[bi]
          transactionId: undefined as any, // TODO[bi]
          isPartner: undefined as any, // TODO[bi]
          amount: undefined as any, // TODO[bi]
        });
      }
      return {
        errors: isError ? [createApplePayErrorConstructorParams(error)] : undefined,
        newTotal,
        newLineItems,
        newShippingMethods: shippingMethods,
      };
    } catch (err) {
      const newTotal = { label: data.paymentLabel, amount: data.paymentAmount };
      return {
        errors: [createApplePayErrorConstructorParams(ShippingError.SHIPPING_UNKNOWN)],
        newTotal,
      };
    }
  };
